import _ from 'lodash';
import { useRouter } from 'next/router';
import React, { memo, useEffect, useState } from 'react';
import FullWidthContainer from '../Layout/Containers/FullWidthContainer';
import TripleColumnContainer from '../Layout/Containers/TripleColumnContainer';
import PageHeaderComponent from '../util/PageHeader/PageHeaderComponent';
import { Tag } from '../util/TagList/TagComponent';
import TagListComponent from '../util/TagList/TagListComponent';
import ResourceComponent from './Resource/ResourceComponent';
import styles from './resources.module.scss';
import { ResourceCategory } from '../Models/ResourceProps';

export interface ResourceProps {
    resourceCategories: ResourceCategory[];
}

const ResourcesComponent = ({ resourceCategories }: ResourceProps) => {
    const router = useRouter();

    const getTags = () => {
        let tags: Array<Tag> = [];
        resourceCategories.forEach((category: ResourceCategory) => {
            if (category.count > 0) {
                tags.push({
                    id: category.id,
                    tagKey: category.key,
                    name: category.name,
                    count: category.count,
                    showCount: true,
                });
            }
        });
        return tags;
    };

    const getDefaultActiveTagsFromUrl = () => {
        let types: Array<string> = []; //WORKS, EVENTS....
        if (router.query.type) {
            if (Array.isArray(router.query.type)) {
                types = router.query.type;
            } else {
                types.push(router.query.type);
            }
        }

        return resourceCategories.filter((t) => types.includes(t.key)).map((t) => t.id);
    };

    const buildUrl = (categories: Array<string>) => {
        const queryString = categories
            .map((type) => {
                return `type=${type}`;
            })
            .join('&');

        if (categories.length > 0) {
            router.push(`?${queryString}`, undefined, { shallow: true });
        } else {
            router.replace('/resources', undefined, { shallow: true });
        }
    };

    const buildRequestParams = (types: Array<string>) => {
        // filters[$or][0][date][$eq]=2020-01-01&filters[$or][1][date][$eq]=2020-01-02
        const filtersQueryString = types
            .map((type, index) => {
                return `filters[$or][${index}][resource_category][key][$eq]=${type}`;
            })
            .join('&');
        return `${filtersQueryString}&populate=*`;
    };

    const [resourceTypes, setResourceTypes] = useState([]);
    const [resources, setResources] = useState<Array<Resource>>([]);
    const [isLoading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);

        let types: Array<string> = []; //WORKS, EVENTS....
        if (router.query.type) {
            if (Array.isArray(router.query.type)) {
                types = router.query.type;
            } else {
                types.push(router.query.type);
            }
        }

        const paramsQuery = buildRequestParams(types);

        Promise.all([
            fetch(
                `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/resources${
                    paramsQuery != '' ? `?${paramsQuery}` : ''
                }`
            ).then((res) => res.json()),
        ]).then(([resources]) => {
            const result = _.orderBy(
                [...resources.data],
                [(obj: any) => new Date(obj.publishedAt)],
                ['desc']
            );
            setResources(result);
            setLoading(false);
        });
    }, [router.query]);

    useEffect(() => {
        const types: Array<string> = resourceTypes.map((typeId) => {
            const category = resourceCategories.find((c) => c.id === typeId);
            return category && category.key;
        });

        buildUrl(types);
    }, [resourceTypes]);

    const onSelectCategory = (activeCategories: Array<number>) => {
        setResourceTypes(activeCategories);
    };

    return (
        <section>
            <PageHeaderComponent
                extraClassNames={styles.ingressContainer}
                hideQuoteOnMobile={false}
            >
                <h1>
                    Regular updates, <br />
                    events and insights{' '}
                </h1>
            </PageHeaderComponent>
            <TagListComponent
                tags={getTags()}
                onSelect={onSelectCategory}
                defaults={getDefaultActiveTagsFromUrl()}
            />
            <FullWidthContainer>
                <div className={styles.resourcesContainer}>
                    <TripleColumnContainer>
                        {resources.map((resource, index) => {
                            const {
                                authorName,
                                heading,
                                ingress,
                                mainImage,
                                seoUrl,
                                resource_category,
                                publishedAt,
                            } = resource;
                            return (
                                <ResourceComponent
                                    key={heading + index}
                                    mainImage={mainImage}
                                    heading={heading}
                                    ingress={ingress}
                                    resource_category={resource_category}
                                    seoUrl={seoUrl}
                                    authorName={authorName}
                                    publishedAt={publishedAt}
                                />
                            );
                        })}
                    </TripleColumnContainer>
                </div>
            </FullWidthContainer>
        </section>
    );
};

export default memo(ResourcesComponent);
